import { isNotDefined } from '@sgme/fp';
import type { ColTypeDef, ValueFormatterParams } from 'ag-grid-community';
import { isToday } from 'date-fns';
import { useSPIntl } from 'hooks/useIntl';
import { useCallback, useMemo } from 'react';

import {
  dateTimeFormatOptions,
  defaultFilterOptions,
  makeDateFilterComparatorWithParser,
  makeGridDateSortComparatorWithParser,
  parseDate,
  parseDateTime,
} from './utils';

export function useFormatDateOrTime() {
  const { formatDate, formatTime } = useSPIntl();
  return useCallback(
    ({ value }: ValueFormatterParams) => {
      if (isNotDefined(value)) {
        return '';
      }
      const dateValue = new Date(value);
      if (isToday(dateValue)) {
        return formatTime(dateValue, {
          hour: 'numeric' as const,
          minute: 'numeric' as const,
          second: 'numeric' as const,
        });
      }
      return formatDate(dateValue, dateTimeFormatOptions);
    },
    [formatDate, formatTime],
  );
}

export function useDateOrTimeColumnType<T>(): ColTypeDef<T> {
  const valueFormatter = useFormatDateOrTime();

  return useMemo(
    () => ({
      comparator: makeGridDateSortComparatorWithParser(parseDateTime),
      valueFormatter,
      filter: 'agDateColumnFilter',
      filterParams: {
        comparator: makeDateFilterComparatorWithParser(parseDate),
        filterOptions: defaultFilterOptions,
        inRangeInclusive: true,
      },
    }),
    [valueFormatter],
  );
}

export function useServerSideDateOrTimeColumnType<T>(): ColTypeDef<T> {
  const valueFormatter = useFormatDateOrTime();

  return useMemo(
    () => ({
      comparator: makeGridDateSortComparatorWithParser(parseDateTime),
      valueFormatter,
    }),
    [valueFormatter],
  );
}
